import { useState } from "react";
import api from "@/lib/api";
import { Label } from "@/components/ui/label";
import { Upload, X, Loader2, Play } from "lucide-react";
import { toast } from "sonner";

const GESTURES = ["wave", "wiggle", "dance", "spin", "jump", "nod", "shake", "bow", "peek", "cheer", "droop"];
const MOODS = [["ecstatic", "Ecstatic"], ["happy", "Happy"], ["neutral", "Neutral"], ["sad", "Sad"], ["mad", "Mad"]];

/** Upload your own animation clips per gesture / mood (.fbx Mixamo, .vrma, .glb). Built-in moves stay as fallback. */
export const AvatarAnims = ({ cfg, setCfg, onPlay }) => {
  const [busy, setBusy] = useState("");
  const anims = cfg.anims || {};
  const upload = async (slot, file) => {
    if (!file) return; setBusy(slot);
    try { const fd = new FormData(); fd.append("file", file); setCfg((await api.post(`/avatar/anim/${slot}`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 600000 })).data); toast.success(`${slot === "idle" ? "idle" : slot.replace("mood_", "")} animation set`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); } finally { setBusy(""); }
  };
  const clear = async (slot) => { try { setCfg((await api.delete(`/avatar/anim/${slot}`)).data); } catch (e) {} };
  const Slot = ({ slot, label, testid }) => {
    const a = anims[slot];
    return (
      <div className={`flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs ${a ? "border-primary/50 bg-primary/5" : "border-white/10"}`} data-testid={testid}>
        <button className="capitalize font-medium hover:text-primary flex items-center gap-1" title="Preview" onClick={() => onPlay?.(slot)} data-testid={`${testid}-play`}><Play className="w-3 h-3" />{label}</button>
        {a ? <><span className="text-muted-foreground truncate max-w-[120px]" title={a.name}>{a.name}</span><button className="text-red-300 hover:text-red-200" onClick={() => clear(slot)} data-testid={`${testid}-clear`}><X className="w-3 h-3" /></button></>
          : <span className="text-muted-foreground">built-in</span>}
        <label className="ml-auto cursor-pointer text-muted-foreground hover:text-primary" title="Upload a clip">
          {busy === slot ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
          <input type="file" accept=".fbx,.vrma,.glb,.gltf" className="hidden" disabled={!!busy} onChange={(e) => upload(slot, e.target.files?.[0])} data-testid={`${testid}-file`} />
        </label>
      </div>
    );
  };
  return (
    <div className="rounded-lg border border-white/10 bg-[#0a0a0a] p-4 space-y-3" data-testid="avatar-anims-box">
      <Label className="text-xs font-800 tracking-widest">YOUR ANIMATIONS</Label>
      <div className="text-[11px] text-muted-foreground">Replace any built-in move with your own clip: <b>.fbx from Mixamo</b> (download "Without Skin"), <b>.vrma</b>, or <b>.glb</b>. The idle clip loops whenever she's just standing there (the built-in breathe & sway is used if empty); mood clips take over while she's in that mood; gesture clips play once. Click a name to preview it on her.</div>
      <div className="text-[11px] font-medium">Idle</div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-1.5"><Slot slot="idle" label="Breathe & sway" testid="anim-idle" /></div>
      <div className="text-[11px] font-medium pt-1">Gestures</div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-1.5">{GESTURES.map((g) => <Slot key={g} slot={g} label={g} testid={`anim-${g}`} />)}</div>
      <div className="text-[11px] font-medium pt-1">Moods (idle loop while she feels that way)</div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-1.5">{MOODS.map(([k, l]) => <Slot key={k} slot={`mood_${k}`} label={l} testid={`anim-mood-${k}`} />)}</div>
    </div>
  );
};
